import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminSectionBaseComponent } from 'src/app/shared/admin-section-base';
import { ConfirmDeleteDialogComponent } from '../shared/confirm-delete-dialog/confirm-delete-dialog.component';
import {
  AdminApiService,
  Demo,
  DemoFile,
  DemoTopic,
  GitHubFile,
  Topic,
  TopicArea,
} from 'src/app/shared/services/admin-api.service';

@Component({
  standalone: false,
  selector: 'app-manage-demos',
  templateUrl: './manage-demos.component.html',
  styleUrls: ['./manage-demos.component.scss'],
})
export class ManageDemosComponent extends AdminSectionBaseComponent implements OnInit {
  demos: Demo[] = [];
  demoFiles: DemoFile[] = [];
  topicAreas: TopicArea[] = [];
  topics: Topic[] = [];
  demoTopics: DemoTopic[] = [];
  githubFiles: GitHubFile[] = [];

  demoForm: Partial<Demo> = { available: 'P', demo_type: '' };
  private originalDemoForm: Partial<Demo> = {};
  demoFileForm: Partial<DemoFile> = { demo_type: 'notebook' };
  private originalDemoFileForm: Partial<DemoFile> = {};

  editingDemoId: number | null = null;
  creatingDemo = false;
  editingDemoFileKey: { label: string } | null = null;
  creatingDemoFile = false;
  topicSearchTerm = '';
  topicAreaFilterForDemo: number | null = null;
  demosSearchTerm = '';

  constructor(
    private adminApiService: AdminApiService,
    private dialog: MatDialog,
    snackBar: MatSnackBar,
    cdr: ChangeDetectorRef,
  ) {
    super(snackBar, cdr);
    this.initPagination('demos', 10);
    this.initPagination('demoFiles', 10);
  }

  ngOnInit(): void {
    this.refreshAll();
    this.trackApiCall(this.adminApiService.fetchGitHubJupyterDemosFiles()).subscribe({
      next: (files) => { this.githubFiles = files; },
      error: (error) => console.error('Failed to fetch GitHub files:', error),
    });
  }

  refreshAll(): void {
    this.trackApiCall(this.adminApiService.bootstrap(this.token)).subscribe({
      next: (data) => {
        this.demos = data.demos;
        this.demoFiles = data.demoFiles;
        this.topicAreas = data.topicAreas;
        this.topics = data.topics;
        this.demoTopics = data.demoTopics;
      },
      error: (error) => this.handleError(error),
    });
  }

  getDisplayedDemos(): Demo[] {
    if (!this.demosSearchTerm.trim()) {
      return this.demos;
    }

    const query = this.demosSearchTerm.toLowerCase();
    return this.demos.filter((demo) => (
      String(demo.id).includes(query)
      || (demo.demo_title || '').toLowerCase().includes(query)
      || (demo.demo_type || '').toLowerCase().includes(query)
      || (demo.available || '').toLowerCase().includes(query)
    ));
  }

  onDemosSearchTermChange(): void {
    this.resetPage('demos');
  }

  getFilteredDirectories(): string[] {
    const input = (this.demoFileForm.directory || '').toLowerCase();
    const dirs = [...new Set(this.githubFiles.map(f => f.path.substring(0, f.path.lastIndexOf('/'))))].filter(Boolean);
    return dirs.filter(d => d.toLowerCase().includes(input)).slice(0, 20);
  }

  getFilteredFilenames(): string[] {
    const dir = (this.demoFileForm.directory || '').trim();
    const input = (this.demoFileForm.filename || '').toLowerCase();
    const candidates = dir
      ? this.githubFiles.filter(f => f.path.substring(0, f.path.lastIndexOf('/')) === dir)
      : this.githubFiles;
    return candidates.map(f => f.name).filter(n => n.toLowerCase().includes(input)).slice(0, 20);
  }

  onGitHubFilenameSelected(filename: string): void {
    const match = this.githubFiles.find(f => f.name === filename);
    if (match && !this.demoFileForm.directory) {
      this.demoFileForm.directory = match.path.substring(0, match.path.lastIndexOf('/'));
    }
  }

  openCreateDemo(): void {
    this.cancelDemoEdit();
    this.creatingDemo = true;
  }

  openEditDemo(item: Demo): void {
    this.editDemo(item);
    this.creatingDemo = false;
    this.resetPage('demoFiles');
  }

  openCreateDemoFileForCurrentDemo(): void {
    if (!this.editingDemoId) {
      this.showError('Open a demo in edit mode first.');
      return;
    }

    this.cancelDemoFileEdit();
    this.demoFileForm.id = this.editingDemoId;
    this.creatingDemoFile = true;
  }

  openEditDemoFile(item: DemoFile): void {
    this.editDemoFile(item);
    this.creatingDemoFile = false;
  }

  isEditingDemoFile(item: DemoFile): boolean {
    return Boolean(
      this.editingDemoFileKey
      && this.editingDemoFileKey.label === item.label,
    );
  }

  isCreatingDemoFile(): boolean {
    return this.creatingDemoFile;
  }

  demoFilesForEditingDemo(): DemoFile[] {
    if (!this.editingDemoId) {
      return [];
    }
    return this.demoFiles.filter((item) => item.id === this.editingDemoId);
  }

  demoTopicsForEditingDemo(): DemoTopic[] {
    if (!this.editingDemoId) {
      return [];
    }

    return this.demoTopics.filter((item) => item.demo_id === this.editingDemoId);
  }

  filteredTopicsForEditingDemo(): Topic[] {
    if (!this.editingDemoId) {
      return [];
    }

    const taggedTopicIds = new Set(this.demoTopicsForEditingDemo().map((item) => item.topic_id));
    const query = this.topicSearchTerm.trim().toLowerCase();
    const topicAreaFilter = this.topicAreaFilterForDemo;

    return this.topics
      .filter((item) => !taggedTopicIds.has(item.id))
      .filter((item) => topicAreaFilter === null || item.topic_area === topicAreaFilter)
      .filter((item) => !query || item.topic_title.toLowerCase().includes(query))
      .slice(0, 20);
  }

  topicTitleById(topicId: number): string {
    const topic = this.topics.find((item) => item.id === topicId);
    return topic ? topic.topic_title : `Topic #${topicId}`;
  }

  topicAreaTitleById(topicAreaId: number | null | undefined): string {
    if (topicAreaId === null || topicAreaId === undefined) {
      return 'Unassigned topic area';
    }

    const topicArea = this.topicAreas.find((item) => item.id === topicAreaId);
    return topicArea ? topicArea.topic_area_title : `Topic Area #${topicAreaId}`;
  }

  topicAreaTitleByTopicId(topicId: number): string {
    const topic = this.topics.find((item) => item.id === topicId);
    return this.topicAreaTitleById(topic?.topic_area);
  }

  addTopicToEditingDemo(topic: Topic): void {
    if (!this.editingDemoId) {
      this.showError('Open a demo in edit mode first.');
      return;
    }

    const payload: Partial<DemoTopic> = {
      demo_id: this.editingDemoId,
      topic_id: topic.id,
      sequence: null,
    };

    const actionKey = this.topicAddActionKey(topic.id);

    this.trackActionCall(actionKey, this.adminApiService.createDemoTopic(this.token, payload)).subscribe({
      next: () => {
        this.topicSearchTerm = '';
        this.showSuccess('Topic tagged to demo successfully.');
        this.trackActionCall(actionKey, this.adminApiService.listDemoTopics(this.token)).subscribe((items) => (this.demoTopics = items));
      },
      error: (error) => this.handleError(error),
    });
  }

  removeTopicFromEditingDemo(item: DemoTopic): void {
    if (!this.editingDemoId) {
      this.showError('Open a demo in edit mode first.');
      return;
    }

    const actionKey = this.topicRemoveActionKey(item.topic_id);

    this.trackActionCall(actionKey, this.adminApiService.deleteDemoTopic(this.token, this.editingDemoId, item.topic_id)).subscribe({
      next: () => {
        this.showSuccess('Topic removed from demo successfully.');
        this.trackActionCall(actionKey, this.adminApiService.listDemoTopics(this.token)).subscribe((items) => (this.demoTopics = items));
      },
      error: (error) => this.handleError(error),
    });
  }

  saveDemo(): void {
    if (!this.demoForm.demo_title || !this.demoForm.demo_type || !this.demoForm.available) {
      this.showError('demo_title, demo_type and available are required.');
      return;
    }

    const { id: _ignoredId, ...demoFormWithoutId } = this.demoForm;

    const payload: Partial<Demo> = {
      ...demoFormWithoutId,
      short_description: this.toNullableString(demoFormWithoutId.short_description),
      follow_up: this.toNullableString(demoFormWithoutId.follow_up),
      demo_title: (demoFormWithoutId.demo_title || '').trim(),
      demo_type: (demoFormWithoutId.demo_type || '').trim(),
    };

    const isUpdate = Boolean(this.editingDemoId);

    const request = this.editingDemoId
      ? this.adminApiService.updateDemo(this.token, this.editingDemoId, payload)
      : this.adminApiService.createDemo(this.token, payload);

    const actionKey = this.demoSaveActionKey();

    this.trackActionCall(actionKey, request).subscribe({
      next: (savedDemo: Demo) => {
        this.showSuccess(`Demo ${isUpdate ? 'updated' : 'created'} successfully.`);
        if (isUpdate) {
          this.trackActionCall(actionKey, this.adminApiService.listDemos(this.token)).subscribe((items) => (this.demos = items));
          return;
        }

        this.creatingDemo = false;
        this.editDemo(savedDemo);
        this.trackActionCall(actionKey, this.adminApiService.listDemos(this.token)).subscribe((items) => (this.demos = items));
      },
      error: (error) => this.handleError(error),
    });
  }

  private normalizeDemoAvailability(value: string | null | undefined): 'Y' | 'N' | 'P' {
    const normalizedValue = (value || '').trim().toUpperCase();
    if (normalizedValue === 'N' || normalizedValue === 'P') {
      return normalizedValue;
    }

    return 'Y';
  }

  editDemo(item: Demo): void {
    this.editingDemoId = item.id;
    const normalizedDemo = { ...item, available: this.normalizeDemoAvailability(item.available) };
    this.demoForm = normalizedDemo;
    this.originalDemoForm = normalizedDemo;
    this.topicSearchTerm = '';
    this.topicAreaFilterForDemo = null;
    this.cancelDemoFileEdit();
  }

  cancelDemoEdit(): void {
    this.cancelDemoFileEdit();
    this.topicSearchTerm = '';
    this.topicAreaFilterForDemo = null;
    this.creatingDemo = false;
    this.editingDemoId = null;
    this.demoForm = { available: 'P', demo_type: '' };
    this.originalDemoForm = {};
  }

  get isDemoEditorVisible(): boolean {
    return this.creatingDemo || Boolean(this.editingDemoId);
  }

  get isDemoFormDirty(): boolean {
    if (!this.editingDemoId) { return false; }
    return (this.demoForm.demo_title || '').trim() !== (this.originalDemoForm.demo_title || '').trim()
      || (this.demoForm.demo_type || '').trim() !== (this.originalDemoForm.demo_type || '').trim()
      || (this.demoForm.available || '') !== (this.originalDemoForm.available || '')
      || this.toNullableString(this.demoForm.short_description) !== this.toNullableString(this.originalDemoForm.short_description)
      || this.toNullableString(this.demoForm.follow_up) !== this.toNullableString(this.originalDemoForm.follow_up);
  }

  deleteDemo(item: Demo): void {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Delete Demo',
        message: 'Are you sure you want to permanently delete this demo?',
        itemName: item.demo_title || `Demo ${item.id}`
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const actionKey = this.demoDeleteActionKey(item.id);
        this.trackActionCall(actionKey, this.adminApiService.deleteDemo(this.token, item.id)).subscribe({
          next: () => {
            this.showSuccess('Demo deleted successfully.');
            if (this.editingDemoId === item.id) {
              this.cancelDemoEdit();
            }
            this.trackActionCall(actionKey, this.adminApiService.listDemos(this.token)).subscribe((items) => (this.demos = items));
          },
          error: (error) => this.handleError(error),
        });
      }
    });
  }

  saveDemoFile(): void {
    if (!this.editingDemoId) {
      this.showError('Open a demo in edit mode first.');
      return;
    }

    if (!this.demoFileForm.label || !this.demoFileForm.directory || !this.demoFileForm.filename || !this.demoFileForm.demo_type) {
      this.showError('label, demo_type, directory and filename are required.');
      return;
    }

    const parentDemoId = this.editingDemoId;

    const payload: Partial<DemoFile> = {
      ...this.demoFileForm,
      id: parentDemoId,
      label: (this.demoFileForm.label || '').trim(),
      directory: (this.demoFileForm.directory || '').trim(),
      filename: (this.demoFileForm.filename || '').trim(),
      demo_type: (this.demoFileForm.demo_type || 'notebook').toLowerCase() as 'nbviewer' | 'notebook',
      programming_language: this.toNullableString(this.demoFileForm.programming_language),
      author: this.toNullableString(this.demoFileForm.author),
      curator: this.toNullableString(this.demoFileForm.curator),
      Language: this.toNullableString(this.demoFileForm.Language),
    };

    const request = this.editingDemoFileKey
      ? this.adminApiService.updateDemoFile(this.token, parentDemoId, this.editingDemoFileKey.label, payload)
      : this.adminApiService.createDemoFile(this.token, payload);

    const actionKey = this.demoFileSaveActionKey();

    this.trackActionCall(actionKey, request).subscribe({
      next: () => {
        this.showSuccess(`Demo file ${this.editingDemoFileKey ? 'updated' : 'created'} successfully.`);
        this.cancelDemoFileEdit();
        this.trackActionCall(actionKey, this.adminApiService.listDemoFiles(this.token)).subscribe((items) => (this.demoFiles = items));
      },
      error: (error) => this.handleError(error),
    });
  }

  editDemoFile(item: DemoFile): void {
    this.editingDemoFileKey = { label: item.label };
    this.originalDemoFileForm = { ...item };
    this.demoFileForm = { ...item, id: this.editingDemoId || item.id };
  }

  cancelDemoFileEdit(): void {
    this.editingDemoFileKey = null;
    this.creatingDemoFile = false;
    this.demoFileForm = { id: this.editingDemoId || undefined, demo_type: 'notebook' };
    this.originalDemoFileForm = {};
  }

  get isDemoFileFormDirty(): boolean {
    if (!this.editingDemoFileKey) { return false; }
    return (this.demoFileForm.label || '').trim() !== (this.originalDemoFileForm.label || '').trim()
      || (this.demoFileForm.demo_type || '').trim() !== (this.originalDemoFileForm.demo_type || '').trim()
      || (this.demoFileForm.directory || '').trim() !== (this.originalDemoFileForm.directory || '').trim()
      || (this.demoFileForm.filename || '').trim() !== (this.originalDemoFileForm.filename || '').trim();
  }

  deleteDemoFile(item: DemoFile): void {
    if (!this.editingDemoId) {
      this.showError('Open a demo in edit mode first.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Delete Demo File',
        message: 'Are you sure you want to permanently delete this demo file?',
        itemName: item.label || 'Demo File'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const actionKey = this.demoFileDeleteActionKey(item.label);
        this.trackActionCall(actionKey, this.adminApiService.deleteDemoFile(this.token, this.editingDemoId!, item.label)).subscribe({
          next: () => {
            this.showSuccess('Demo file deleted successfully.');
            if (this.isEditingDemoFile(item)) {
              this.cancelDemoFileEdit();
            }
            this.trackActionCall(actionKey, this.adminApiService.listDemoFiles(this.token)).subscribe((items) => (this.demoFiles = items));
          },
          error: (error) => this.handleError(error),
        });
      }
    });
  }

  refreshActionKey(): string {
    return 'refresh-demos';
  }

  demoSaveActionKey(): string {
    return 'demo-save';
  }

  demoDeleteActionKey(demoId: number): string {
    return `demo-delete:${demoId}`;
  }

  demoFileSaveActionKey(): string {
    return 'demo-file-save';
  }

  demoFileDeleteActionKey(label: string): string {
    return `demo-file-delete:${this.editingDemoId || 'none'}:${label}`;
  }

  topicAddActionKey(topicId: number): string {
    return `demo-topic-add:${this.editingDemoId || 'none'}:${topicId}`;
  }

  topicRemoveActionKey(topicId: number): string {
    return `demo-topic-remove:${this.editingDemoId || 'none'}:${topicId}`;
  }
}
